"use client";
import React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { ChartData } from "@/types/types";
import { useTranslations } from 'next-intl';
import ChartCard from "@/components/chart-card";

/*
D1 - green
Q1 - blue
Q2 - yellow
Q3 - orange
Q4 - red
N/A - gray
*/

interface ChartDisplayProps {
  chartData: ChartData;
}

const ChartDisplay: React.FC<ChartDisplayProps> = ({ chartData }) => {
  const t = useTranslations('charts');

  // Vibrant colors (Tailwind-esque) for a modern look
  const rankingColors = {
    D1: '#10B981',
    Q1: '#3B82F6',
    Q2: '#F59E0B',
    Q3: '#F97316',
    Q4: '#EF4444',
    'N/A': '#9CA3AF',
  };

  const buildRankingSeries = (data: {
    years: string[];
    rankings: { D1: number[]; Q1: number[]; Q2: number[]; Q3: number[]; Q4: number[]; 'N/A': number[] };
  }) => [
    { label: 'D1', data: data.rankings.D1, color: rankingColors.D1, stack: 'rankings' },
    { label: 'Q1', data: data.rankings.Q1, color: rankingColors.Q1, stack: 'rankings' },
    { label: 'Q2', data: data.rankings.Q2, color: rankingColors.Q2, stack: 'rankings' },
    { label: 'Q3', data: data.rankings.Q3, color: rankingColors.Q3, stack: 'rankings' },
    { label: 'Q4', data: data.rankings.Q4, color: rankingColors.Q4, stack: 'rankings' },
    { label: 'N/A', data: data.rankings['N/A'], color: rankingColors['N/A'], stack: 'rankings' },
  ];

  const limitYears = (years: string[], maxCount = 25) => {
    const now = new Date().getFullYear()
    const validYears = years.filter((y) => {
      const n = parseInt(y, 10)
      return !Number.isNaN(n) && n <= now
    })
    if (validYears.length <= maxCount) return { years: validYears, start: years.length - validYears.length }
    const start = validYears.length - maxCount
    return { years: validYears.slice(start), start: years.indexOf(validYears[start]) }
  }

  const sliceRanking = (src: {
    years: string[];
    rankings: { D1: number[]; Q1: number[]; Q2: number[]; Q3: number[]; Q4: number[]; 'N/A': number[] };
  }, maxCount = 25) => {
    const { years, start } = limitYears(src.years, maxCount)
    return {
      years,
      rankings: {
        D1: src.rankings.D1.slice(start),
        Q1: src.rankings.Q1.slice(start),
        Q2: src.rankings.Q2.slice(start),
        Q3: src.rankings.Q3.slice(start),
        Q4: src.rankings.Q4.slice(start),
        'N/A': src.rankings['N/A'].slice(start),
      },
    }
  }


  const computeStackMax = (data: { years: string[]; rankings: { D1: number[]; Q1: number[]; Q2: number[]; Q3: number[]; Q4: number[]; 'N/A': number[] } }) => {
    if (!data.years.length) return undefined
    const totals = data.rankings.D1.map((_, i) =>
      (data.rankings.D1[i] || 0) +
      (data.rankings.Q1[i] || 0) +
      (data.rankings.Q2[i] || 0) +
      (data.rankings.Q3[i] || 0) +
      (data.rankings.Q4[i] || 0) +
      (data.rankings['N/A'][i] || 0)
    )
    const sorted = [...totals].sort((a, b) => a - b)
    const idx = Math.max(0, Math.floor(sorted.length * 0.95) - 1)
    const p95 = sorted[idx]
    const maxVal = sorted[sorted.length - 1]
    const suggested = Math.max(1, Math.ceil(((p95 || maxVal) as number) * 1.1))
    return suggested
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {chartData.publicationsWithRankings && chartData.publicationsWithRankings.years.length > 0 && (
        <ChartCard title={t('publicationsByRankingYear')}>
          {(() => {
            const sliced = sliceRanking(chartData.publicationsWithRankings, 20)
            const ymax = computeStackMax(sliced)
            return (
              <BarChart
                style={{ width: '100%', height: '100%' }}
                xAxis={[{
                  scaleType: 'band',
                  data: sliced.years,
                  valueFormatter: (v: string | number) => String(v),
                  height: 50,
                }]}
                yAxis={[{ min: 0, max: ymax }]}
                series={buildRankingSeries(sliced)}
                height={undefined}
                margin={{ top: 28, right: 20, bottom: 26, left: 18 }}
                axisHighlight={{ x: 'band' }}
                grid={{ horizontal: true }}
                slotProps={{
                  legend: { position: { vertical: 'top', horizontal: 'center' } },
                  tooltip: { trigger: 'axis' },
                }}
              />
            )
          })()}
        </ChartCard>
      )}
      {chartData.cumulativePublications && chartData.cumulativePublications.years.length > 0 && (
        <ChartCard title={t('cumulativePublications')}>
          {(() => {
            const sliced = sliceRanking(chartData.cumulativePublications, 20)
            const ymax = computeStackMax(sliced)
            return (
              <BarChart
                style={{ width: '100%', height: '100%' }}
                xAxis={[{
                  scaleType: 'band',
                  data: sliced.years,
                  valueFormatter: (v: string | number) => String(v),
                  height: 50,
                }]}
                yAxis={[{ min: 0, max: ymax }]}
                series={buildRankingSeries(sliced)}
                height={undefined}
                margin={{ top: 28, right: 20, bottom: 26, left: 18 }}
                axisHighlight={{ x: 'band' }}
                grid={{ horizontal: true }}
                slotProps={{
                  legend: { position: { vertical: 'top', horizontal: 'center' } },
                  tooltip: { trigger: 'axis' },
                }}
              />
            )
          })()}
        </ChartCard>
      )}
      {chartData.iScore && chartData.iScore.years.length > 0 && (
        <ChartCard title={t('iScoreByYear')}>
          <BarChart
            style={{ width: '100%', height: '100%' }}
            xAxis={[{
              scaleType: 'band',
              data: chartData.iScore.years,
              valueFormatter: (v: string | number) => String(v),
              height: 50,
            }]}
            yAxis={[{ min: 0 }]}
            series={[{ label: chartData.iScore.label, data: chartData.iScore.data as number[], color: '#06B6D4' }]}
            height={undefined}
            margin={{ top: 28, right: 20, bottom: 26, left: 20 }}
            axisHighlight={{ x: 'band' }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: { position: { vertical: 'top', horizontal: 'center' } },
              tooltip: { trigger: 'axis' },
            }}
          />
        </ChartCard>
      )}
      {chartData.cumulativeIScore && chartData.cumulativeIScore.years.length > 0 && (
        <ChartCard title={t('cumulativeIScore')}>
          <BarChart
            style={{ width: '100%', height: '100%' }}
            xAxis={[{
              scaleType: 'band',
              data: chartData.cumulativeIScore.years,
              valueFormatter: (v: string | number) => String(v),
              height: 50,
            }]}
            yAxis={[{ min: 0 }]}
            series={[{ label: chartData.cumulativeIScore.label, data: chartData.cumulativeIScore.data as number[], color: '#22C55E' }]}
            height={undefined}
            margin={{ top: 28, right: 20, bottom: 26, left: 20 }}
            axisHighlight={{ x: 'band' }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: { position: { vertical: 'top', horizontal: 'center' } },
              tooltip: { trigger: 'axis' },
            }}
          />
        </ChartCard>
      )}
      {chartData.qScore && chartData.qScore.years.length > 0 && (
        <ChartCard title={t('qScoreByYear')}>
          <BarChart
            style={{ width: '100%', height: '100%' }}
            xAxis={[{
              scaleType: 'band',
              data: chartData.qScore.years,
              valueFormatter: (v: string | number) => String(v),
              height: 50,
            }]}
            yAxis={[{ min: 0 }]}
            series={[{
              label: chartData.qScore.label,
              data: chartData.qScore.data as number[],
              color: '#F59E0B',
              valueFormatter: (v: number | null) => (v == null ? 'N/A' : v.toFixed(3)),
            }]}
            height={undefined}
            margin={{ top: 28, right: 20, bottom: 26, left: 20 }}
            axisHighlight={{ x: 'band' }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: { position: { vertical: 'top', horizontal: 'center' } },
              tooltip: { trigger: 'axis' },
            }}
          />
        </ChartCard>
      )}
      {chartData.cumulativeQScore && chartData.cumulativeQScore.years.length > 0 && (
        <ChartCard title={t('cumulativeQScore')}>
          <BarChart
            style={{ width: '100%', height: '100%' }}
            xAxis={[{
              scaleType: 'band',
              data: chartData.cumulativeQScore.years,
              valueFormatter: (v: string | number) => String(v),
              height: 50,
            }]}
            yAxis={[{ min: 0 }]}
            series={[{
              label: chartData.cumulativeQScore.label,
              data: chartData.cumulativeQScore.data as number[],
              color: '#EF4444',
              valueFormatter: (v: number | null) => (v == null ? 'N/A' : v.toFixed(3)),
            }]}
            height={undefined}
            margin={{ top: 28, right: 20, bottom: 26, left: 20 }}
            axisHighlight={{ x: 'band' }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: { position: { vertical: 'top', horizontal: 'center' } },
              tooltip: { trigger: 'axis' },
            }}
          />
        </ChartCard>
      )}
      {chartData.impactFactor && chartData.impactFactor.years.length > 0 && (
        <ChartCard title={t('impactFactorByYear')}>
          <BarChart
            style={{ width: '100%', height: '100%' }}
            xAxis={[{
              scaleType: 'band',
              data: chartData.impactFactor.years,
              valueFormatter: (v: string | number) => String(v),
              height: 50,
            }]}
            yAxis={[{ min: 0 }]}
            series={[{ label: chartData.impactFactor.label, data: chartData.impactFactor.data as number[], color: '#3B82F6' }]}
            height={undefined}
            margin={{ top: 28, right: 20, bottom: 26, left: 20 }}
            axisHighlight={{ x: 'band' }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: { position: { vertical: 'top', horizontal: 'center' } },
              tooltip: { trigger: 'axis' },
            }}
          />
        </ChartCard>
      )}
      {chartData.cumulativeImpactFactor && chartData.cumulativeImpactFactor.years.length > 0 && (
        <ChartCard title={t('cumulativeImpactFactor')}>
          <BarChart
            style={{ width: '100%', height: '100%' }}
            xAxis={[{
              scaleType: 'band',
              data: chartData.cumulativeImpactFactor.years,
              valueFormatter: (v: string | number) => String(v),
              height: 50,
            }]}
            yAxis={[{ min: 0 }]}
            series={[{ label: chartData.cumulativeImpactFactor.label, data: chartData.cumulativeImpactFactor.data as number[], color: '#7C3AED' }]}
            height={undefined}
            margin={{ top: 28, right: 20, bottom: 26, left: 20 }}
            axisHighlight={{ x: 'band' }}
            grid={{ horizontal: true }}
            slotProps={{
              legend: { position: { vertical: 'top', horizontal: 'center' } },
              tooltip: { trigger: 'axis' },
            }}
          />
        </ChartCard>
      )}
    </div>
  );
};

export default ChartDisplay;
